// Disabling sort-key for fields that follow the expected order of columns in the generated report
/* eslint-disable sort-keys */
import { PermissionEnum } from "@saleor/types/globalTypes";
import Moment from "moment";

export const payoutUserRole = permission => {
  const code = permission.map(e => e.code);

  const userPerm = [
    {
      filename: "Open_Order",
      fields: {
        order_date: "Order Created Date",
        completed_at: "Order Completion Date",
        store_order_code: "Store Order Code",
        payment_method_type: "Payment Method",
        total_retail_price: "Total Gross Amount",
        sale_discount_tenant: "Tenant Discount",
        sale_discount_sm: "SMO Discount",
        sale_discount_total: "Total Discount",
        total_selling_price: "Total Sales Amount",
        delivery_fee: "Delivery Fee",
        total_gross_transaction: "Total Gross Transaction",
        voucher_discount_tenant: "Tenant Voucher",
        voucher_discount_sm: "SMO Voucher",
        voucher_discount_total: "Total Voucher",
        price_paid: "Price Paid",
        payment_fee: "Payment Fee",
        payment_fee_vat: "VAT on Payment Fee",
        payment_fee_adjustment: "Adjustment on Payment Fee",
        payment_fee_adjustment_vat: "Adjustment VAT on Payment Fee",
        total_payment_fee: "Total Payment Fee",
        payment_fee_witholding_tax: "Withholding Tax on Payment Fee",
        credit_card_witholding_tax: "Witholding Tax on Credit Card Transaction",
        other_adjustment: "Other Adjustment",
        net_settlement: "Net Settlement",
        settlement_date: "Settlement Date"
      },
      perm: PermissionEnum.MANAGE_PAYOUT_OPEN_ORDER,
      userRole: "specialist",
      sheetName: "Open Order",
      title: "Open Order",
      button: "DOWNLOAD"
    },
    {
      filename: "Payable List",
      fields: {
        company_name: "Company Name",
        coverage_start_date: "Cut Off Start Date",
        coverage_end_date: "Cut Off End Date",
        brand_name: "Brand",
        mall_name: "Mall",
        net_settlement: "Net Settlement",
        settlement_date: "Settlement Date"
      },
      perm: PermissionEnum.MANAGE_PAYOUT_ON_PROCESS,
      userRole: "manager",
      sheetName: "Payable List",
      title: "Payable List",
      button: "GENERATE PAYABLE LIST"
    },
    {
      filename: "Payment_File",
      fields: {
        bank_account_no: "Bank Account No",
        bank_account_name: "Bank Account Name",
        net_settlement: "Amount",
        customer_code: "Reference (Customer Number)"
      },
      perm: PermissionEnum.MANAGE_PAYOUT_PAYABLES,
      userRole: "treasury1",
      sheetName: "Payment File",
      title: "Payment File",
      button: "GENERATE BANK FILE"
    },
    {
      filename: "Payment_File",
      fields: {
        bank_account_no: "Bank Account No",
        bank_account_name: "Bank Account Name",
        net_settlement: "Amount",
        customer_code: "Reference (Customer Number)"
      },
      perm: PermissionEnum.MANAGE_PAYOUT_PAYMENT,
      userRole: "treasury2",
      sheetName: "Payment File",
      title: "Payment File",
      button: "GENERATE PAYMENT FILE"
    }
  ];

  const result = userPerm.filter(e => code.includes(e.perm));

  if (result && result.length === 1) {
    return result[0];
  } else {
    return userPerm[0];
  }
};

// This function is dynamically used for the first button in the payout modules
export const selectReportData = (reportData, payoutUser) =>
  filterReportData(reportData, payoutUser.fields);

export const selectedRow = (permission, status) => {
  const code = permission.map(e => e.code);
  const data = [
    {
      perm: PermissionEnum.MANAGE_PAYOUT_OPEN_ORDER,
      payoutStatus: ["Open Order", "Schedule for Payout", "Returned"]
    },
    {
      perm: PermissionEnum.MANAGE_PAYOUT_ON_PROCESS,
      payoutStatus: ["On Process"]
    },
    {
      perm: PermissionEnum.MANAGE_PAYOUT_PAYABLES,
      payoutStatus: ["Payment processing", "For Payment"]
    },
    {
      perm: PermissionEnum.MANAGE_PAYOUT_PAYMENT,
      payoutStatus: ["Ready for payment", "Completed", "Payment processing"]
    }
  ];

  const result = data.filter(e => code.includes(e.perm));

  if (result && result.length > 1) {
    return true;
  }

  return result[0].payoutStatus.includes(status);
};

export const managerDownloadData = {
  filename: "Payout_Report",
  fields: {
    order_date: "Order Created Date",
    completed_at: "Order Completion Date",
    store_order_code: "Store Order Code",
    payment_method_type: "Payment Method",
    total_retail_price: "Total Gross Amount",
    sale_discount_tenant: "Tenant Discount",
    sale_discount_sm: "SMO Discount",
    sale_discount_total: "Total Discount",
    total_selling_price: "Total Sales Amount",
    delivery_fee: "Delivery Fee",
    total_gross_transaction: "Total Gross Transaction",
    voucher_discount_tenant: "Tenant Voucher",
    voucher_discount_sm: "SMO Voucher",
    voucher_discount_total: "Total Voucher",
    price_paid: "Price Paid",
    payment_fee: "Payment Fee",
    payment_fee_vat: "VAT on Payment Fee",
    payment_fee_adjustment: "Adjustment on Payment Fee",
    payment_fee_adjustment_vat: "Adjustment VAT on Payment Fee",
    total_payment_fee: "Total Payment Fee",
    payment_fee_witholding_tax: "Withholding Tax on Payment Fee",
    credit_card_witholding_tax: "Witholding Tax on Credit Card Transaction",
    other_adjustment: "Other Adjustment",
    net_settlement: "Net Settlement",
    settlement_status: "Settlement Status",
    settlement_date: "Settlement Date"
  },
  sheetName: "Payment Report",
  title: "Payment Report"
};

// Filter report data
export const filterReportData = (reportData, reportFields) => {
  const filteredData = reportData.map(data => {
    delete data.__typename;

    data.coverage_start_date = data?.coverage_start_date
      ? Moment.tz(new Date(data?.coverage_start_date), "Asia/Manila").format(
          "YYYY-MM-DD"
        )
      : null;

    data.coverage_end_date = data?.coverage_end_date
      ? Moment.tz(new Date(data?.coverage_end_date), "Asia/Manila").format(
          "YYYY-MM-DD"
        )
      : null;

    data.order_date = data?.order_date
      ? Moment.tz(new Date(data?.order_date), "Asia/Manila").format(
          "YYYY-MM-DD HH:mm:ss"
        )
      : null;
    data.completed_at = data?.completed_at
      ? Moment.tz(new Date(data?.completed_at), "Asia/Manila").format(
          "YYYY-MM-DD HH:mm:ss"
        )
      : null;
    data.settlement_date = data?.settlement_date
      ? Moment.tz(new Date(data?.settlement_date), "Asia/Manila").format(
          "YYYY-MM-DD"
        )
      : null;
    data.payment_method_type = data?.payment_method_type?.toUpperCase();

    // Filters object to remove extra column
    const erase = Object.keys(data).filter(
      e => !Object.keys(reportFields).includes(e)
    );

    // Deletes here
    for (const i in erase) {
      if ({}?.hasOwnProperty.call(erase, i)) {
        delete data[`${erase[i]}`];
      }
    }
    return data;
  });

  return filteredData;
};
